import { useEffect, useState } from 'react';
import { Container, Typography, Card, CardContent, Box, Button, Alert, CircularProgress, Chip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface PerkItem {
  id: number;
  title: string;
  description?: string;
  discount_type?: string;
  discount_value?: string;
  terms?: string;
  merchant: {
    name: string;
    logo?: string;
    website?: string;
    location?: { address?: string; city?: string; state?: string; zip?: string };
  };
  valid_until?: string | null;
}

const PerksPage = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [perks, setPerks] = useState<PerkItem[]>([]);
  const [qr, setQr] = useState<string | null>(null);
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/api/perks`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!res.ok) {
          setMessage(data.error || 'Perks unavailable');
          setPerks([]);
        } else {
          setPerks(data.perks || []);
          setQr(data.user_qr_code || null);
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load perks');
      } finally {
        setLoading(false);
      }
    };
    if (user) load();
  }, [user]);

  const refreshQR = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/perks/qr`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) setQr(data.qr_data);
    } catch {}
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Perks</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Exclusive benefits for verified medical professionals in eligible regions.
      </Typography>

      {loading && (
        <Box textAlign="center" py={4}><CircularProgress /></Box>
      )}

      {!loading && !user && (
        <Alert severity="info">Sign in to view available perks.</Alert>
      )}

      {!loading && user && message && (
        <Alert severity="warning">{message}</Alert>
      )}

      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {qr && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">Your Verification Code</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Show this code at participating businesses. A QR visual will be added soon.
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace' }}>{qr}</Box>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={refreshQR}>Refresh Code</Button>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
        {perks.map((p) => (
          <Card key={p.id}>
            <CardContent>
              <Typography variant="h6">{p.title}</Typography>
              {p.description && <Typography variant="body2" color="text.secondary">{p.description}</Typography>}
              <Box sx={{ mt: 1 }}>
                {p.discount_type && (
                  <Chip label={`${p.discount_type}${p.discount_value ? `: ${p.discount_value}` : ''}`} size="small" sx={{ mr: 1 }} />
                )}
                {p.valid_until && (
                  <Chip label={`Valid until ${p.valid_until}`} size="small" />
                )}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {p.merchant.name}{p.merchant.location?.city ? ` — ${p.merchant.location.city}, ${p.merchant.location.state}` : ''}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Container>
  );
};

export default PerksPage;
